import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { ArrowLeft, Calendar, Loader2, AlertCircle } from "lucide-react";
import { toast } from "react-toastify";
import {
  StepFormContainer,
  StepOneTypeSelection,
  StepTwoOrganizerSelection,
  StepThreeInformationSection,
  StepFourReview,
  StepFourConferenceTickets,
} from "./components";
import {
  transformConferenceTradeMissionPayload,
  transformConferenceTradeMissionToFormData,
} from "./utils/payloadTransformer";
import eventService from "../../../../api/EventService";
import organizerService from "../../../../api/OrganizerService";

const EditConferenceTradeMission = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { event_slug } = useParams();
  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(!location.state?.eventData);
  const [error, setError] = useState(null);
  const [eventType, setEventType] = useState(null);
  const [sourcePage, setSourcePage] = useState(null);
  const [formData, setFormData] = useState({
    organizer: null,
    // Step 3 data
    eventStartDate: "",
    eventEndDate: "",
    eventStartTime: "",
    eventEndTime: "",
    venueName: "",
    venueStreetAddress: "",
    country: "",
    city: "",
    venueGoogleLocation: "",
    thumbnail: null,
    featuredImages: [],
    aboutEvent: "",
    performers: [],
    sponsors: [],
    itinerary: [],
    // Conditional fields
    totalParticipantSlots: "",
    motivationalQuestions: [],
    // Conference ticket fields
    overallSlots: "",
    ticketTypes: [],
  });

  // Set source page from location state
  useEffect(() => {
    if (location.state?.from) {
      setSourcePage(location.state.from);
    } else if (location.state?.eventType) {
      const eventType = location.state.eventType;
      setSourcePage(
        eventType === "conference"
          ? "/admin/media/conferences"
          : eventType === "trade_mission"
          ? "/admin/media/trade-missions"
          : null
      );
    }
  }, [location.state]);

  // Fetch event data
  useEffect(() => {
    const fetchEventData = async () => {
      // If event data was passed via state, use it
      if (location.state?.eventData) {
        const eventData = location.state.eventData;

        // Determine event type
        const eventTypes = Array.isArray(eventData.event_type)
          ? eventData.event_type
          : [eventData.event_type];
        const isConference = eventTypes.includes("conference");
        const isTradeMission = eventTypes.includes("trade_mission");
        const detectedType = isConference ? "conference" : isTradeMission ? "trade_mission" : null;

        setEventType(detectedType);

        // Fetch organizer data if organizer_slug exists
        let organizer = null;
        if (eventData.organizer_slug) {
          try {
            const organizersResponse = await organizerService.getOrganizers();
            if (organizersResponse.data?.status_code === 200) {
              const organizersList = Array.isArray(organizersResponse.data.data)
                ? organizersResponse.data.data
                : organizersResponse.data.data || [];
              organizer = organizersList.find(
                (org) => org.slug === eventData.organizer_slug
              );
            }
          } catch (orgError) {
            console.warn("Could not fetch organizer:", orgError);
          }
        }

        // Transform backend data to frontend format
        const transformedData = transformConferenceTradeMissionToFormData(eventData, organizer);

        if (transformedData) {
          setFormData(transformedData);
        } else {
          setError("Failed to transform event data");
          toast.error("Failed to transform event data");
        }
        setIsLoading(false);
        return;
      }

      // Otherwise, fetch from API (fallback for direct navigation)
      if (!event_slug) {
        setError("Event identifier is missing");
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        // Fetch event data
        const eventResponse = await eventService.getEvent(event_slug);

        if (!eventResponse.status || !eventResponse.data) {
          throw new Error(eventResponse.message || "Failed to fetch event");
        }

        // Handle different response structures
        let eventData = null;
        if (eventResponse.data.status_code === 200 && eventResponse.data.data) {
          eventData = eventResponse.data.data;
        } else if (eventResponse.data && !eventResponse.data.status_code) {
          eventData = eventResponse.data;
        }

        if (!eventData) {
          throw new Error("Event data not found");
        }

        // Determine event type
        const eventTypes = Array.isArray(eventData.event_type)
          ? eventData.event_type
          : [eventData.event_type];
        const isConference = eventTypes.includes("conference");
        const isTradeMission = eventTypes.includes("trade_mission");
        const detectedType = isConference ? "conference" : isTradeMission ? "trade_mission" : null;

        if (!detectedType) {
          throw new Error("This event is not a conference or trade mission");
        }

        setEventType(detectedType);

        // Fetch organizer data if organizer_slug exists
        let organizer = null;
        if (eventData.organizer_slug) {
          try {
            const organizersResponse = await organizerService.getOrganizers();
            if (organizersResponse.data?.status_code === 200) {
              const organizersList = Array.isArray(organizersResponse.data.data)
                ? organizersResponse.data.data
                : organizersResponse.data.data || [];
              organizer = organizersList.find(
                (org) => org.slug === eventData.organizer_slug
              );
            }
          } catch (orgError) {
            console.warn("Could not fetch organizer:", orgError);
          }
        }

        // Transform backend data to frontend format
        const transformedData = transformConferenceTradeMissionToFormData(eventData, organizer);

        if (transformedData) {
          setFormData(transformedData);
        } else {
          throw new Error("Failed to transform event data");
        }
      } catch (err) {
        const errorMessage =
          err.message || "An error occurred while loading event data";
        setError(errorMessage);
        toast.error(errorMessage);
      } finally {
        setIsLoading(false);
      }
    };

    fetchEventData();
  }, [event_slug, location.state]);

  const totalSteps = eventType === "conference" ? 5 : 4;
  const stepTitles = [
    "Select Type",
    "Select Organizer",
    eventType === "conference"
      ? "Conference Information"
      : eventType === "trade_mission"
      ? "Trade Mission Information"
      : "Information",
    eventType === "conference" ? "Conference Tickets" : "Review & Submit",
    eventType === "conference" ? "Review & Submit" : "",
  ].filter(Boolean);

  const handleTypeSelect = (type) => {
    const previousType = eventType;
    setEventType(type);

    // If switching types, clear incompatible fields
    if (previousType && previousType !== type) {
      if (previousType === "conference" && type === "trade_mission") {
        // Switching from conference to trade mission - remove tickets
        setFormData((prev) => ({
          ...prev,
          overallSlots: "",
          ticketTypes: [],
          motivationalQuestions: [],
        }));
        toast.info("Switched to Trade Mission. Ticket data has been cleared.");
      } else if (previousType === "trade_mission" && type === "conference") {
        // Switching from trade mission to conference - remove participant slots
        setFormData((prev) => ({
          ...prev,
          totalParticipantSlots: "",
        }));
        toast.info("Switched to Conference. Participant slots cleared. Please add ticket information.");
      }
    }
  };

  const handleStepOneNext = () => {
    if (eventType) {
      setCurrentStep(2);
    } else {
      toast.error("Please select a type");
    }
  };

  const handleOrganizerSelect = (organizer) => {
    setFormData((prev) => ({
      ...prev,
      organizer,
    }));
  };

  const handleStepTwoNext = () => {
    if (formData.organizer) {
      setCurrentStep(3);
    } else {
      toast.error("Please select an organizer");
    }
  };

  const handleStepTwoBack = () => {
    setCurrentStep(1);
  };

  const handleStepThreeNext = () => {
    if (eventType === "conference") {
      setCurrentStep(4); // Go to tickets step
    } else {
      setCurrentStep(4); // Go to review step
    }
  };

  const handleStepThreeBack = () => {
    setCurrentStep(2);
  };

  const handleStepFourNext = () => {
    if (eventType === "conference") {
      setCurrentStep(5); // Go to review step
    }
  };

  const handleStepFourBack = () => {
    if (eventType === "conference") {
      setCurrentStep(3); // Back to information step
    } else {
      setCurrentStep(3); // Back to information step
    }
  };

  const handleStepFiveBack = () => {
    if (eventType === "conference") {
      setCurrentStep(4); // Back to tickets step
    }
  };

  const handleSubmitEvent = async (data) => {
    if (!event_slug) {
      toast.error("Event identifier is missing");
      return;
    }

    const payload = transformConferenceTradeMissionPayload(data, eventType);

    if (!payload.thumbnail) {
      toast.error("Please upload a thumbnail image");
      return;
    }

    // Validation based on type
    if (eventType === "conference") {
      if (!payload.overall_slots || payload.overall_slots <= 0) {
        toast.error("Please specify overall slots for the conference");
        return;
      }
      if (!payload.ticket_types || payload.ticket_types.length === 0) {
        toast.error("Please add at least one ticket type for the conference");
        return;
      }
    } else if (eventType === "trade_mission") {
      if (!payload.overall_slots || payload.overall_slots <= 0) {
        toast.error("Please specify total participant slots for the trade mission");
        return;
      }
    }

    try {
      setIsLoading(true);
      const response = await eventService.updateEvent(event_slug, payload);

      if (response.status && response.data?.status_code === 200) {
        toast.success(
          response.data.reason ||
            response.message ||
            `${eventType === "conference" ? "Conference" : "Trade Mission"} updated successfully!`
        );
        navigate("/admin/media/conference");
      } else {
        let errorMessage = response.message || `Failed to update ${eventType === "conference" ? "conference" : "trade mission"}`;
        
        if (response.errors) {
          const fieldErrors = Object.values(response.errors).flat();
          if (fieldErrors.length > 0) {
            errorMessage = fieldErrors.join(", ");
          }
        }
        
        toast.error(errorMessage);
      }
    } catch (err) {
      const errorData = err?.response?.data;
      let errorMessage = `An error occurred while updating ${eventType === "conference" ? "conference" : "trade mission"}`;

      if (errorData) {
        if (errorData.message) {
          errorMessage = errorData.message;
        } else if (errorData.reason) {
          errorMessage = errorData.reason;
        }

        // Include field-specific errors if available
        if (errorData.errors) {
          const fieldErrors = Object.values(errorData.errors).flat();
          if (fieldErrors.length > 0) {
            errorMessage = fieldErrors.join(", ");
          }
        }
      }

      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFormDataUpdate = (updatedData) => {
    setFormData((prev) => ({
      ...prev,
      ...updatedData,
    }));
  };

  // Scroll to top when step changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [currentStep]);

  // Loading state
  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-center">
          <Loader2 className="mx-auto mb-4 w-12 h-12 text-purple-600 animate-spin" />
          <p className="text-gray-600">Loading event data...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="px-6 py-4">
        <button
          onClick={() => navigate("/admin/media/conference")}
          className="flex items-center mb-6 text-gray-600 transition-colors hover:text-gray-900 group"
        >
          <ArrowLeft className="mr-2 w-4 h-4 transition-transform group-hover:-translate-x-1" />
          <span className="text-sm font-medium">Back to Conferences & Trade Missions</span>
        </button>

        <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
          <div className="p-4 mb-4 bg-red-100 rounded-full">
            <AlertCircle className="w-12 h-12 text-red-600" />
          </div>
          <h2 className="mb-2 text-xl font-bold text-gray-900">
            Error Loading Event
          </h2>
          <p className="mb-6 text-gray-600">{error}</p>
          <button
            onClick={() => navigate("/admin/media/conference")}
            className="px-6 py-2 text-white bg-purple-600 rounded-lg transition-colors hover:bg-purple-700"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Header */}
      <div className="px-6 py-4 mb-6">
        <button
          onClick={() => {
            // Navigate back to source page if available, otherwise use eventType
            if (sourcePage) {
              navigate(sourcePage);
            } else if (eventType === "conference") {
              navigate("/admin/media/conferences");
            } else if (eventType === "trade_mission") {
              navigate("/admin/media/trade-missions");
            } else {
              navigate("/admin/media/conference");
            }
          }}
          className="flex items-center mb-3 text-gray-600 transition-colors hover:text-gray-900 group"
        >
          <ArrowLeft className="mr-2 w-4 h-4 transition-transform group-hover:-translate-x-1" />
          <span className="text-sm font-medium">
            {sourcePage === "/admin/media/conferences" || eventType === "conference"
              ? "Back to Conferences"
              : sourcePage === "/admin/media/trade-missions" || eventType === "trade_mission"
              ? "Back to Trade Missions"
              : "Back to Conferences & Trade Missions"}
          </span>
        </button>

        <div className="flex items-center space-x-3">
          <div className="p-2 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg shadow">
            <Calendar className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900 sm:text-2xl">
              Edit {eventType === "conference" ? "Conference" : eventType === "trade_mission" ? "Trade Mission" : "Event"}
            </h1>
            <p className="mt-0.5 text-xs text-gray-600">
              Update details for your {eventType === "conference" ? "conference" : eventType === "trade_mission" ? "trade mission" : "event"}
            </p>
          </div>
        </div>
      </div>

      <StepFormContainer
        currentStep={currentStep}
        totalSteps={totalSteps}
        stepTitles={stepTitles}
        showProgress={true}
      >
        {/* Step Content */}
        {currentStep === 1 && (
          <StepOneTypeSelection
            selectedType={eventType}
            onSelect={handleTypeSelect}
            onNext={handleStepOneNext}
          />
        )}

        {currentStep === 2 && (
          <StepTwoOrganizerSelection
            selectedOrganizer={formData.organizer}
            onSelect={handleOrganizerSelect}
            onNext={handleStepTwoNext}
            onBack={handleStepTwoBack}
          />
        )}

        {currentStep === 3 && (
          <StepThreeInformationSection
            formData={formData}
            onUpdate={handleFormDataUpdate}
            onBack={handleStepThreeBack}
            onNext={handleStepThreeNext}
            eventType={eventType}
          />
        )}

        {currentStep === 4 && eventType === "conference" && (
          <StepFourConferenceTickets
            formData={formData}
            onUpdate={handleFormDataUpdate}
            onBack={handleStepFourBack}
            onNext={handleStepFourNext}
          />
        )}

        {currentStep === (eventType === "conference" ? 5 : 4) && (
          <StepFourReview
            formData={formData}
            onBack={eventType === "conference" ? handleStepFiveBack : handleStepFourBack}
            onSubmit={handleSubmitEvent}
            eventType={eventType}
          />
        )}
      </StepFormContainer>
    </>
  );
};

export default EditConferenceTradeMission;
